import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import PageHeader from "../../components/system/PageHeader";
import Card from "../../components/system/Card";
import EmptyState from "../../components/system/EmptyState";
import { emailToFriendlyName } from "../../lib/operatorName";
import { History as HistoryIcon } from "lucide-react";

const RECENT_DAYS = 5;
const recentCutoffMs = () => Date.now() - RECENT_DAYS * 24 * 60 * 60 * 1000;

export default function HistoryPage() {
  const [items, setItems] = useState([]);
  const [view, setView] = useState("recent");

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/action-history"); setItems(Array.isArray(data) ? data : []); }
      catch (e) { toast.error(formatApiErrorDetail(e)); }
    })();
  }, []);

  const { recent, history, recentCount, historyCount } = useMemo(() => {
    const cutoff = recentCutoffMs();
    const r = [], h = [];
    for (const it of items) {
      const t = new Date(it.created_at).getTime();
      (Number.isFinite(t) && t >= cutoff ? r : h).push(it);
    }
    return { recent: r, history: h, recentCount: r.length, historyCount: h.length };
  }, [items]);

  const visible = view === "recent" ? recent : history;

  return (
    <div data-testid="admin-history-page">
      <PageHeader eyebrow="Audit log" title="Action History" />

      <div className="flex gap-2 mb-4" data-testid="history-view-tabs">
        {[
          { k: "recent",  l: `Recent · last ${RECENT_DAYS}d`, n: recentCount },
          { k: "history", l: "History",                       n: historyCount },
        ].map(({ k, l, n }) => (
          <button
            key={k}
            onClick={() => setView(k)}
            className={[
              "h-10 px-4 rounded-button font-display text-xs uppercase tracking-wide inline-flex items-center gap-2 transition-all duration-150 active:scale-[0.98]",
              view === k
                ? "bg-ink text-white shadow-rest"
                : "bg-paper-2 text-ink-2 shadow-rest hover:shadow-raised",
            ].join(" ")}
            data-testid={`history-view-${k}`}
          >
            {l}
            <span className={`font-mono text-[10px] ${view === k ? "text-white/70" : "text-ink-3"}`}>{n}</span>
          </button>
        ))}
      </div>

      {visible.length === 0 ? (
        <EmptyState
          icon={<HistoryIcon size={36} strokeWidth={1.5} />}
          title={view === "recent" ? "No recent actions" : "No older actions"}
          description={view === "recent" ? `Nothing has been logged in the last ${RECENT_DAYS} days.` : "History is empty."}
          data-testid="history-empty"
        />
      ) : (
        <Card variant="raised" className="overflow-hidden">
          {visible.map((a, idx) => (
            <div
              key={a.id}
              className={`flex items-center justify-between gap-3 p-4 ${idx > 0 ? "border-t border-border/40" : ""}`}
              data-testid={`history-row-${a.id}`}
            >
              <div className="min-w-0">
                <div className="font-mono text-xs uppercase tracking-wider">{a.action_type}</div>
                <div
                  className="data-sku truncate"
                  title={a.actor_email}
                  data-testid={`history-actor-${a.id}`}
                >
                  {emailToFriendlyName(a.actor_email)} · {a.target_type || "—"} {a.target_id ? "· " + a.target_id.slice(0, 8) : ""}
                </div>
                {a.metadata && Object.keys(a.metadata).length > 0 && (
                  <pre className="text-[10px] text-muted-foreground mt-1 truncate">{JSON.stringify(a.metadata)}</pre>
                )}
              </div>
              <div className="font-mono text-xs text-muted-foreground whitespace-nowrap">{new Date(a.created_at).toLocaleString()}</div>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}
